import { useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PANEL_WIDTH_VAR } from '../../hooks/useResizablePanel';
import { usePublishDetailPanel } from '../../hooks/useDetailPanel';

/** What a page's list keeps beside a dragged-wide panel. */
export const PAGE_LIST_MIN_WIDTH = 420;

/**
 * Options for `useResizablePanel` on a page whose panel opens at a share of
 * the page rather than at a fixed width — Accounts, Categories, Tags, Source
 * files. The property stays unset until the reader drags, so the panel keeps
 * opening exactly where it always did.
 */
export const pagePanelOptions = (minWidth) => ({
  defaultWidth: null,
  minWidth,
  maxWidth: Infinity,
  listMinWidth: PAGE_LIST_MIN_WIDTH,
});

/**
 * The panel's width, in CSS so it follows a window resize without a render.
 * Read inside out: the dragged width if there is one, else the page's usual
 * share; never wider than what leaves the list its minimum; never narrower
 * than the panel's own floor.
 */
export const resizablePanelWidth = (share, minWidth) =>
  `max(${minWidth}px, min(var(${PANEL_WIDTH_VAR}, ${share}), calc(100% - ${PAGE_LIST_MIN_WIDTH}px)))`;

/**
 * The drag handle on a detail panel's left edge — the Movements page's handle,
 * shared so every page's panel is sized the same way.
 *
 * It sits in the border, out of the panel's flow, so nothing inside has to
 * make room for it, and stays invisible until reached for. The panel it sits
 * in must be `position: relative` on desktop.
 */
export default function PanelResizeHandle({ resize }) {
  const { t } = useTranslation('movements');
  const { isResizing, startResize, onHandleKeyDown, resetWidth } = resize;
  // The handle is only mounted with its panel, so while it is here the panel
  // is open: the floating chat moves aside by the panel's width.
  const handleRef = useRef(null);
  usePublishDetailPanel(handleRef);
  return (
    <Box
      ref={handleRef}
      display={{ base: 'none', md: 'block' }}
      position="absolute"
      left="-3px"
      top={0}
      bottom={0}
      w="6px"
      cursor="col-resize"
      zIndex={2}
      role="separator"
      aria-orientation="vertical"
      aria-label={t('list.detailPanel.resize')}
      tabIndex={0}
      onPointerDown={startResize}
      onKeyDown={onHandleKeyDown}
      onDoubleClick={resetWidth}
      _hover={{ bg: 'redesign.accent.blue', opacity: 0.35 }}
      _focusVisible={{ bg: 'redesign.accent.blue', opacity: 0.6, outline: 'none' }}
      bg={isResizing ? 'redesign.accent.blue' : 'transparent'}
      opacity={isResizing ? 0.6 : 1}
      transition="background 0.12s, opacity 0.12s"
    />
  );
}
